import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  CalendarClock, RefreshCcw, Plus, Trash2, Pause, Play, Clock, X, Search,
  CircleCheck, CircleAlert,
} from 'lucide-react';
import {
  The, DauThe, TheSoLieu, NutPhu, NutChinh, NhanTrangThai, ChuaCoDuLieu, DangTai,
} from '../components/ui';
import { api, LoiMayChu } from '../services/api';
import type { LichDinhKy, Nhom, TrangThaiExtension } from '../services/kieu';

/**
 * Màn Lịch định kỳ — đặt lịch đăng bài lặp lại theo khung giờ trong ngày.
 *
 * BA ĐIỂM NGHIỆP VỤ PHẢI NÓI ĐÚNG VỚI NGƯỜI DÙNG:
 *
 * 1. Lịch chạy theo GIỜ VIỆT NAM (UTC+7), không theo giờ máy người dùng.
 *    Bộ chạy nền tự quy đổi (recurringScheduler.js:43-60).
 *
 * 2. Nếu Extension offline đúng khung giờ thì lịch BỎ QUA khung đó, KHÔNG chạy bù.
 *    Đây là chủ đích: chạy bù sẽ dồn một loạt bài khi máy bật lại
 *    (recurringScheduler.js:101-105). Người dùng cần biết để không chờ đợi nhầm.
 *
 * 3. Trạng thái COMPLETED do máy chủ tự đặt khi quá ngày kết thúc — người dùng
 *    chỉ bật/tắt được giữa ACTIVE và PAUSED (recurring.routes.js:86).
 */

interface Props {
  ext: TrangThaiExtension | null;
  onLamMoi: () => void;
}

/** Bộ chạy nền so khớp đúng chuỗi "HH:mm", nên ô giờ phải sinh ra đúng dạng này. */
function chuanGio(v: string): string | null {
  const m = String(v || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const gio = Number(m[1]);
  const phut = Number(m[2]);
  if (gio < 0 || gio > 23 || phut < 0 || phut > 59) return null;
  return String(gio).padStart(2, '0') + ':' + m[2];
}

function gioNgan(ms?: number | null) {
  if (!ms) return '—';
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })
    + ' ' + d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

export function ManLichDinhKy({ ext, onLamMoi }: Props) {
  const [lich, setLich] = useState<LichDinhKy[]>([]);
  const [nhom, setNhom] = useState<Nhom[]>([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState<string | null>(null);
  const [dangLam, setDangLam] = useState<string | null>(null);
  const [moForm, setMoForm] = useState(false);

  // --- Dữ liệu biểu mẫu tạo lịch ---
  const [ten, setTen] = useState('');
  const [noiDung, setNoiDung] = useState('');
  const [daChonNhom, setDaChonNhom] = useState<Set<string>>(new Set());
  const [timNhom, setTimNhom] = useState('');
  const [cacGio, setCacGio] = useState<string[]>([]);
  const [gioMoi, setGioMoi] = useState('08:00');
  const [tuNgay, setTuNgay] = useState('');
  const [denNgay, setDenNgay] = useState('');
  const [anDanh, setAnDanh] = useState(false);
  const [neSpam, setNeSpam] = useState(true);
  const [tuGiay, setTuGiay] = useState(60);
  const [denGiay, setDenGiay] = useState(120);
  const [dangGui, setDangGui] = useState(false);

  const tai = useCallback(async () => {
    setDangTai(true);
    const [ld, nh] = await Promise.allSettled([api.lichDinhKy(), api.nhom()]);
    if (ld.status === 'fulfilled') setLich(ld.value.schedules || []);
    if (nh.status === 'fulfilled') setNhom(nh.value.groups || []);

    const hong = [ld, nh].find(x => x.status === 'rejected') as PromiseRejectedResult | undefined;
    setLoi(hong ? (hong.reason instanceof LoiMayChu ? hong.reason.message : 'Một phần dữ liệu chưa tải được.') : null);
    setDangTai(false);
  }, []);

  useEffect(() => { tai(); }, [tai]);

  const uid = ext?.uid ? String(ext.uid) : '';

  const nhomLoc = useMemo(() => {
    const tu = timNhom.trim().toLowerCase();
    if (!tu) return nhom;
    return nhom.filter(n => (n.name || '').toLowerCase().includes(tu) || String(n.id).includes(tu));
  }, [nhom, timNhom]);

  // Kiểm tra tại chỗ, nêu đúng lý do — để khách không bị máy chủ từ chối
  // sau khi đã soạn xong.
  const loiHopLe = useMemo((): string | null => {
    if (!noiDung.trim()) return 'Chưa nhập nội dung bài viết.';
    if (daChonNhom.size === 0) return 'Chưa chọn nhóm nào.';
    if (cacGio.length === 0) return 'Chưa thêm khung giờ nào.';
    if (!uid) return 'Chưa nhận diện tài khoản Facebook. Mở Chrome và đăng nhập.';
    if (tuNgay && denNgay && tuNgay > denNgay) return 'Ngày bắt đầu phải trước ngày kết thúc.';
    if (denGiay < tuGiay) return 'Giãn cách kết thúc phải lớn hơn hoặc bằng giãn cách bắt đầu.';
    return null;
  }, [noiDung, daChonNhom, cacGio, uid, tuNgay, denNgay, tuGiay, denGiay]);

  const themGio = () => {
    const g = chuanGio(gioMoi);
    if (!g) { setLoi('Giờ không hợp lệ. Dùng dạng HH:mm, ví dụ 08:30.'); return; }
    if (cacGio.includes(g)) { setLoi('Khung giờ này đã có trong danh sách.'); return; }
    setCacGio(c => [...c, g].sort());
    setLoi(null);
  };

  const donForm = () => {
    setTen(''); setNoiDung(''); setDaChonNhom(new Set()); setTimNhom('');
    setCacGio([]); setGioMoi('08:00'); setTuNgay(''); setDenNgay('');
    setAnDanh(false); setNeSpam(true); setTuGiay(60); setDenGiay(120);
  };

  const guiForm = async () => {
    if (loiHopLe) { setLoi(loiHopLe); return; }
    setDangGui(true);
    try {
      await api.taoLichDinhKy({
        name: ten.trim() || undefined,
        message: noiDung.trim(),
        targets: [...daChonNhom],
        times: cacGio,
        fbUid: uid,
        isAnonymous: anDanh,
        safeMode: neSpam,
        delayFromSec: tuGiay,
        delayToSec: denGiay,
        startDate: tuNgay || null,
        endDate: denNgay || null,
      });
      donForm();
      setMoForm(false);
      setLoi(null);
      await tai();
      onLamMoi();
    } catch (e) {
      setLoi(e instanceof LoiMayChu ? e.message : 'Không tạo được lịch.');
    } finally {
      setDangGui(false);
    }
  };

  const doiTrangThai = async (l: LichDinhKy) => {
    // COMPLETED là trạng thái máy chủ tự đặt, không bật/tắt được.
    if (l.status === 'COMPLETED') return;
    const moi = l.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setDangLam(l.id + 'status');
    try {
      await api.doiTrangThaiLich(l.id, moi);
      await tai();
    } catch (e) {
      setLoi(e instanceof LoiMayChu ? e.message : 'Không đổi được trạng thái.');
    } finally {
      setDangLam(null);
    }
  };

  const xoa = async (l: LichDinhKy) => {
    if (!window.confirm('Xoá lịch này? Các bài chưa đăng theo lịch sẽ không chạy nữa.')) return;
    setDangLam(l.id + 'xoa');
    try {
      await api.xoaLichDinhKy(l.id);
      await tai();
      onLamMoi();
    } catch (e) {
      setLoi(e instanceof LoiMayChu ? e.message : 'Không xoá được lịch.');
    } finally {
      setDangLam(null);
    }
  };

  const soDangChay = lich.filter(l => l.status === 'ACTIVE').length;
  const soTamDung = lich.filter(l => l.status === 'PAUSED').length;
  const soXong = lich.filter(l => l.status === 'COMPLETED').length;

  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in space-y-5">
      {loi && (
        <div className="flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
          <span>{loi}</span>
          <button onClick={() => setLoi(null)} className="shrink-0 text-amber-600 hover:text-amber-800">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <TheSoLieu icon={<Play className="h-5 w-5" />} so={soDangChay} nhan="Lịch đang chạy" />
        <TheSoLieu icon={<Pause className="h-5 w-5" />} so={soTamDung} nhan="Đang tạm dừng" />
        <TheSoLieu icon={<CircleCheck className="h-5 w-5" />} so={soXong} nhan="Đã kết thúc" />
      </div>

      {/* Nói rõ hai điều người dùng dễ hiểu nhầm */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600 dark:border-slate-700/60 dark:bg-slate-800/50 dark:text-slate-400">
        <p>
          <span className="font-semibold">Lịch chạy theo giờ Việt Nam (UTC+7)</span>, không theo giờ máy bạn.
        </p>
        <p className="mt-1">
          Nếu Extension không kết nối đúng khung giờ, lịch <span className="font-semibold">bỏ qua khung đó</span> và
          không đăng bù — để tránh dồn một loạt bài khi máy bật lại.
        </p>
      </div>

      <The>
        <DauThe
          icon={<CalendarClock className="h-4 w-4" />}
          tieuDe="Lịch định kỳ"
          dem={lich.length}
          phai={
            <div className="flex gap-2">
              <NutPhu onClick={() => { tai(); onLamMoi(); }}>
                <RefreshCcw className="h-3.5 w-3.5" />
                Làm mới
              </NutPhu>
              <NutChinh onClick={() => setMoForm(m => !m)}>
                <Plus className="h-4 w-4" />
                {moForm ? 'Đóng' : 'Tạo lịch'}
              </NutChinh>
            </div>
          }
        />

        {moForm && (
          <div className="space-y-4 border-b border-slate-100 bg-slate-50/50 p-5 dark:border-slate-700/60 dark:bg-slate-900/20">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                Tên lịch <span className="font-normal text-slate-400">(không bắt buộc)</span>
              </label>
              <input
                value={ten}
                onChange={e => setTen(e.target.value)}
                placeholder="Ví dụ: Đăng tin bất động sản buổi sáng"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                Nội dung bài viết
              </label>
              <textarea
                value={noiDung}
                onChange={e => setNoiDung(e.target.value)}
                rows={4}
                placeholder="Nội dung sẽ đăng lặp lại theo lịch..."
                className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              />
              <p className="mt-1 text-xs text-slate-400">
                Dùng cú pháp xoay vòng {'{a|b|c}'} để mỗi lần đăng ra một biến thể khác nhau.
              </p>
            </div>

            {/* Khung giờ */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                Khung giờ trong ngày <span className="font-normal text-slate-400">(giờ Việt Nam)</span>
              </label>
              <div className="flex flex-wrap items-center gap-2">
                {cacGio.map(g => (
                  <span
                    key={g}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-50 px-2.5 py-1.5 text-xs font-semibold text-brand-700 dark:bg-brand-600/15 dark:text-brand-400"
                  >
                    <Clock className="h-3 w-3" />
                    {g}
                    <button
                      onClick={() => setCacGio(c => c.filter(x => x !== g))}
                      className="text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="time"
                  value={gioMoi}
                  onChange={e => setGioMoi(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                />
                <NutPhu onClick={themGio}>
                  <Plus className="h-3.5 w-3.5" />
                  Thêm giờ
                </NutPhu>
              </div>
            </div>

            {/* Chọn nhóm */}
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  Nhóm đích
                </label>
                <span className="text-xs text-slate-400">Đã chọn {daChonNhom.size}/{nhom.length}</span>
              </div>
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  value={timNhom}
                  onChange={e => setTimNhom(e.target.value)}
                  placeholder="Tìm nhóm..."
                  className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                />
              </div>
              <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
                {nhomLoc.length === 0 ? (
                  <ChuaCoDuLieu chu="Không có nhóm nào." goiY="Sang màn Tổng quan bấm Làm mới để đồng bộ nhóm." />
                ) : (
                  nhomLoc.map(n => {
                    const chon = daChonNhom.has(String(n.id));
                    return (
                      <button
                        key={n.id}
                        onClick={() => setDaChonNhom(s => {
                          const m = new Set(s);
                          if (m.has(String(n.id))) m.delete(String(n.id)); else m.add(String(n.id));
                          return m;
                        })}
                        className={
                          'flex w-full items-center gap-2.5 border-b border-slate-50 px-3 py-2 text-left text-sm last:border-0 dark:border-slate-700/50 ' +
                          (chon ? 'bg-brand-50 dark:bg-brand-600/10' : 'hover:bg-slate-50 dark:hover:bg-slate-700/30')
                        }
                      >
                        <span
                          className={
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded border ' +
                            (chon ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 dark:border-slate-600')
                          }
                        >
                          {chon && <CircleCheck className="h-3 w-3" />}
                        </span>
                        <span className="truncate text-slate-700 dark:text-slate-200">{n.name || String(n.id)}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Khoảng ngày + giãn cách */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                  Từ ngày <span className="font-normal text-slate-400">(để trống là chạy ngay)</span>
                </label>
                <input
                  type="date"
                  value={tuNgay}
                  onChange={e => setTuNgay(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                  Đến ngày <span className="font-normal text-slate-400">(để trống là chạy mãi)</span>
                </label>
                <input
                  type="date"
                  value={denNgay}
                  onChange={e => setDenNgay(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                  Giãn cách giữa các nhóm (giây)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={10}
                    value={tuGiay}
                    onChange={e => setTuGiay(Number(e.target.value) || 0)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                  <span className="shrink-0 text-xs text-slate-400">đến</span>
                  <input
                    type="number"
                    min={10}
                    value={denGiay}
                    onChange={e => setDenGiay(Number(e.target.value) || 0)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                </div>
              </div>
              <div className="flex items-end gap-4 pb-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input type="checkbox" checked={anDanh} onChange={e => setAnDanh(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
                  Đăng ẩn danh
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input type="checkbox" checked={neSpam} onChange={e => setNeSpam(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
                  Chế độ an toàn
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {loiHopLe
                  ? <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400"><CircleAlert className="h-3.5 w-3.5" />{loiHopLe}</span>
                  : <>Sẽ đăng <span className="font-semibold">{daChonNhom.size}</span> nhóm × <span className="font-semibold">{cacGio.length}</span> khung giờ mỗi ngày.</>}
              </p>
              <NutChinh onClick={guiForm} disabled={Boolean(loiHopLe) || dangGui}>
                {dangGui ? 'Đang tạo...' : 'Tạo lịch'}
              </NutChinh>
            </div>
          </div>
        )}

        {dangTai ? (
          <DangTai dong={3} />
        ) : lich.length === 0 ? (
          <ChuaCoDuLieu chu="Chưa có lịch định kỳ nào." goiY="Bấm Tạo lịch để đặt lịch đăng bài lặp lại." />
        ) : (
          <div className="divide-y divide-slate-50 dark:divide-slate-700/50">
            {lich.map(l => (
              <div key={l.id} className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-semibold text-slate-800 dark:text-slate-100">
                      {l.name || l.message?.slice(0, 50) || '(không có tên)'}
                    </p>
                    {l.status === 'ACTIVE' && <NhanTrangThai chu="Đang chạy" mau="xanh" />}
                    {l.status === 'PAUSED' && <NhanTrangThai chu="Tạm dừng" mau="vang" />}
                    {l.status === 'COMPLETED' && <NhanTrangThai chu="Đã kết thúc" mau="xam" />}
                  </div>
                  <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                    {l.message?.slice(0, 90)}
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {(l.times || []).join(', ') || '—'}
                    </span>
                    <span>{(l.targets || []).length} nhóm</span>
                    {l.isAnonymous && <span className="text-slate-500 dark:text-slate-400">Ẩn danh</span>}
                    {(l.startDate || l.endDate) && (
                      <span>{l.startDate || '…'} → {l.endDate || '…'}</span>
                    )}
                    <span>Chạy gần nhất: {gioNgan(l.lastRunAt)}</span>
                  </div>
                </div>

                <div className="flex shrink-0 gap-1.5">
                  {l.status !== 'COMPLETED' && (
                    <NutPhu onClick={() => doiTrangThai(l)} disabled={dangLam === l.id + 'status'}>
                      {l.status === 'ACTIVE'
                        ? <><Pause className="h-3.5 w-3.5" />Tạm dừng</>
                        : <><Play className="h-3.5 w-3.5" />Bật lại</>}
                    </NutPhu>
                  )}
                  <NutPhu onClick={() => xoa(l)} disabled={dangLam === l.id + 'xoa'}>
                    <Trash2 className="h-3.5 w-3.5" />
                    Xoá
                  </NutPhu>
                </div>
              </div>
            ))}
          </div>
        )}
      </The>
    </div>
  );
}
